import { useCallback, useEffect, useRef, useState } from 'react';

import type { MySkill } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

/**
 * Pure reducer for toggling one skill in the tri-state selection. Exported for
 * unit tests. The subtle rule: when the current selection is `null` ("all"),
 * the first toggle expands to the full available set and removes the toggled
 * one — so unchecking a single skill from "all" keeps every other skill,
 * rather than collapsing the selection to just that one id.
 */
export function toggleSkillSelection(
  current: string[] | null,
  allAvailableIds: string[],
  skillId: string,
): string[] {
  const base = current ?? allAvailableIds;
  return base.includes(skillId) ? base.filter((id) => id !== skillId) : [...base, skillId];
}

/**
 * ViewModel for the composer's per-conversation skill selector.
 *
 * Loads the skills the CURRENT user is entitled to (GET /auth/me/skills — never
 * more than their role grants) and tracks a tri-state selection persisted on
 * the conversation:
 *   - `selected === null`  → unset: the agent sees ALL the user's granted
 *     skills (default behavior; the selector is opt-in narrowing).
 *   - `selected === []`    → explicit none: zero skills.
 *   - `selected = [...]`   → exactly those skills (intersected with grants
 *     server-side, so it can only narrow).
 *
 * The selection is saved back to `conversation.metadata.selected_skill_ids` via
 * PATCH /conversations/{id}. We save on demand (popover close) rather than on
 * every checkbox toggle to avoid a write per click.
 */
export function useConversationSkills(conversationId: string | null) {
  const apiClient = useApiClient();
  const [available, setAvailable] = useState<MySkill[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  // null = unset (all granted); array = explicit selection.
  const [selected, setSelected] = useState<string[] | null>(null);

  // Load the user's entitled skills once (they don't change per conversation).
  const load = useCallback(() => {
    setStatus('loading');
    apiClient
      .getMySkills()
      .then((skills) => {
        setAvailable(skills);
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load skills');
        setStatus('error');
      });
  }, [apiClient]);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
  }, []);

  // Seed the selection from the conversation's persisted value, and re-seed
  // whenever the active conversation changes. Fetching here (rather than via a
  // prop) keeps the hook independent of the list-item shape.
  useEffect(() => {
    if (!conversationId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    apiClient
      .getConversation(conversationId)
      .then((c) => {
        if (!cancelled) setSelected(c.selected_skill_ids ?? null);
      })
      .catch(() => {
        if (!cancelled) setSelected(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, conversationId]);

  const toggle = useCallback(
    (skillId: string) => {
      const allIds = available.map((s) => s.skill_id);
      setSelected((prev) => toggleSkillSelection(prev, allIds, skillId));
    },
    [available],
  );

  const selectAll = useCallback(() => {
    setSelected(available.map((s) => s.skill_id));
  }, [available]);

  const selectNone = useCallback(() => setSelected([]), []);

  // Clear the per-conversation narrowing entirely (back to "all granted").
  const reset = useCallback(() => setSelected(null), []);

  const save = useCallback(async () => {
    if (!conversationId) return;
    try {
      await apiClient.updateConversation(conversationId, { selected_skill_ids: selected });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save skill selection');
    }
  }, [apiClient, conversationId, selected]);

  return { available, status, error, selected, toggle, selectAll, selectNone, reset, save };
}
